/// <amd-module name="WorkItemsHub/WorkItemsHubViewProvider" />

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { WorkItemsHubView } from "Components/WorkItemsHubView";
import { WorkItemsHubStore } from "Stores/WorkItemsHubStore";
import { ActionsCreator } from "Actions/ActionsCreator";
import * as React from "react";
import * as Utils_String from "VSS/Core/Util/String";
import { IPivotBarAction, IPivotBarViewAction, PivotBarViewActionType, IOnOffViewActionProps } from "VSSUI/PivotBar";
import { ObservableArray } from "VSS/Core/Observable";
import { IWorkItemsTabContentData } from "WorkItemsHub/Components/IWorkItemsTabContentData";
import { WorkItemsHubTabs, WorkItemsHubRouteConstants } from "WorkItemsHub/Generated/Constants";
import * as WorkItemsHubTabUtils from "WorkItemsHub/Utils/WorkItemsHubTabUtils";
import { WorkItemsHubShortcutGroup } from "WorkItemsHub/WorkItemsHubShortcutGroup";
import * as Resources from "WorkItemsHub/Resources";
import { UsageTelemetryHelper, PerformanceTelemetryHelper } from "WorkItemsHub/Utils/Telemetry";
import { IViewOptionsValues, VIEW_OPTIONS_CHANGE_EVENT } from "VSSUI/Utilities/ViewOptions";
import { IVssHubViewState, VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { ActionsHub } from "Actions/ActionsHub";
import { WorkItemsHubData } from "Generated/Contracts";
import { WorkItemsHubDataProvider } from "DataProviders/WorkItemsHubDataProvider";
import { IVssHistoryService } from "VSS/Platform/History";
import { WorkItemsHubCommandProvider } from "DataProviders/WorkItemsHubCommandProvider";
import { HubViewOptionKeys, HubViewStateEventNames } from "VSSUI/Utilities/HubViewState";
import { HistoryBehavior } from "VSSUI/Navigation/ViewStateNavigation";
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import * as WorkItemsHubTabSettings from "WorkItemsHub/Utils/WorkItemsHubTabSettings";
import { ActionParameters, ActionUrl } from "WorkItemsHub/Constants";
import { getQueriesHubUrl, getCurrentHashString, deserializeState, getEditWorkItemUrl } from "WorkItemsHub/Utils/UrlUtils";
import { shallowEquals } from "WorkItemsHub/Utils/Array";

const VIEWOPTIONS_SHOW_COMPLETED_ITEMS_KEY: string = "show-completed-items";

const InitalizationStateKeys: string[] = ["filterText", "viewOption"];

// This component renders nothing if it's compat url (with hash fragment) and it will be redirected to proper pages
// Otherwise it renders work items hub view content
export class WorkItemsHubViewComponent extends VssComponent {
    private isCompatUrl: boolean = false;
    constructor(props: never, context: IVssComponentContext) {
        super(props, context);
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const project = tfsPageData.project;

        // Handle back-compat URLs
        const hashFragment = getCurrentHashString();
        if (hashFragment) {
            const hashState = deserializeState(hashFragment);
            const id = hashState[ActionParameters.ID];
            const action = hashState[ActionUrl.ACTION];

            // handle #_a=edit&id=number
            if (id && action && Utils_String.equals(action, ActionUrl.ACTION_EDIT, true)) {
                // redirect to /edit/number
                UsageTelemetryHelper.publishBackCompatUrlOpenWorkItem(this.context.pageContext, hashFragment);
                window.location.href = getEditWorkItemUrl(this.context.pageContext, project.name, id);
                this.isCompatUrl = true;
            }

            // handle back-compat query urls with hash fragment - #_a=query&id=<guid> or #_a=query&path=<path> or #_a=query&wiql=<path>&name=<name>
            // back-compat URLs that have query parameters and navigation paths are handled on the server-side by request handlers
            if (action && Utils_String.equals(action, ActionUrl.ACTION_QUERY, true)) {
                // redirect to queries page with query parameters
                UsageTelemetryHelper.publishBackCompatUrlQuery(this.context.pageContext, hashFragment);
                window.location.href = getQueriesHubUrl(this.context.pageContext, project.name, hashFragment);
                this.isCompatUrl = true;
            }
        }
    }

    public render(): JSX.Element {
        if (this.isCompatUrl) {
            return null;
        }

        return <WorkItemsHubViewProvider />;
    }
}

export class WorkItemsHubViewProvider extends VssComponent {
    private static store: WorkItemsHubStore;
    private static actionsCreator: ActionsCreator;
    private static hubViewState: IVssHubViewState;
    private static workItemsHubCommandProvider: WorkItemsHubCommandProvider;
    private static observableCommands: ObservableArray<IPivotBarAction>;
    private static observableViewActions: ObservableArray<IPivotBarViewAction>;
    private static viewActions: IPivotBarViewAction[];
    private static mountCount: number = 0;

    private _project: IProjectData;

    private _initialTabId: string;

    private _workItemsHubShortcutGroup: WorkItemsHubShortcutGroup;
    private static viewActionCache: { [key: string]: IPivotBarViewAction[] } = {};

    constructor(props: never, context: IVssComponentContext) {
        super(props, context);

        WorkItemsHubViewProvider.mountCount++;

        // VS extension client can pass filter initialization properties through URL parameters "filterText" and "viewOption"
        // Process these parameters here
        const historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        let historyState = historyService.getState();
        const { filterText, viewOption } = historyState;

        if (filterText || viewOption) {
            // Remove filter parameters from the URL so they won't be re-applied if the user refreshes the page
            InitalizationStateKeys.forEach(stateKey => (historyState[stateKey] = undefined));
            historyService.replaceState(historyState);
        }

        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        this._project = tfsPageData.project;

        this._initializeFlux(viewOption);
        this._initView();

        if (filterText != null) {
            WorkItemsHubViewProvider.actionsCreator.updateFilterState(this._initialTabId, { text: { values: [filterText] } });
        }

        PerformanceTelemetryHelper.getInstance(this.context.pageContext).startPageLoad();
        UsageTelemetryHelper.publishLoadWorkItemsHub(this.context.pageContext, this._initialTabId);
    }

    private _initializeFlux(viewOption: string): void {
        const actionsHub = new ActionsHub();
        const hubData: WorkItemsHubData = WorkItemsHubDataProvider.getDataForCurrentTab(this.context.pageContext);
        const defaultTabId = hubData ? hubData.processSettings.tabId : WorkItemsHubTabUtils.TabIdByTabEnumValueMap[WorkItemsHubTabs.AssignedToMe];

        this._setInitialViewAction(defaultTabId);

        let showCompletedItemsOverride = null;
        if (viewOption) {
            if (Utils_String.equals(viewOption, "showCompleted", true)) {
                showCompletedItemsOverride = true;
            } else if (Utils_String.equals(viewOption, "hideCompleted", true)) {
                showCompletedItemsOverride = false;
            }

            if (showCompletedItemsOverride != null) {
                if (hubData && hubData.userSettings && hubData.userSettings.showCompleted !== showCompletedItemsOverride) {
                    WorkItemsHubTabSettings.updateShowCompletedForTab(
                        this.context.pageContext,
                        this._project.id,
                        this._initialTabId,
                        showCompletedItemsOverride
                    );
                }
            }
        }

        WorkItemsHubViewProvider.store = new WorkItemsHubStore(
            actionsHub,
            this.context.pageContext,
            this._initialTabId,
            hubData,
            null,
            showCompletedItemsOverride
        );
        WorkItemsHubViewProvider.actionsCreator = new ActionsCreator(this.context.pageContext, actionsHub);
    }

    private _initView(): void {
        WorkItemsHubViewProvider.hubViewState = new VssHubViewState(this.context.pageContext, {
            defaultPivot: this._initialTabId,
            pivotNavigationParamName: WorkItemsHubRouteConstants.TabRouteParameterName,
            viewOptionNavigationParameters: [{ key: HubViewOptionKeys.showFilterBar, behavior: HistoryBehavior.none }]
        });

        WorkItemsHubViewProvider.hubViewState.viewOptions.setViewOption(HubViewOptionKeys.showFilterBar, true);
        WorkItemsHubViewProvider.hubViewState.viewOptions.setViewOption(WorkItemsHubRouteConstants.TabRouteParameterName, this._initialTabId);
        WorkItemsHubViewProvider.setViewOptions(this._initialTabId);

        WorkItemsHubViewProvider.workItemsHubCommandProvider = new WorkItemsHubCommandProvider(
            this.context.pageContext,
            WorkItemsHubViewProvider.getStore(),
            WorkItemsHubViewProvider.hubViewState,
            WorkItemsHubViewProvider.getActionsCreator()
        );
        WorkItemsHubViewProvider.observableCommands = this._getObservableCommands({ selectionIds: [], hasFilter: false, tabId: this._initialTabId });
        WorkItemsHubViewProvider.viewActions = WorkItemsHubViewProvider.getHubViewActions(this._initialTabId);
        // important: always create a new starting array for ObservableArray since we have caching logic
        WorkItemsHubViewProvider.observableViewActions = new ObservableArray<IPivotBarViewAction>([...WorkItemsHubViewProvider.viewActions]);

        WorkItemsHubViewProvider.hubViewState.subscribe(this._onPivotChanging, HubViewStateEventNames.pivotChanging);
        WorkItemsHubViewProvider.hubViewState.viewOptions.subscribe(this._onViewOptionsChanged, VIEW_OPTIONS_CHANGE_EVENT);
    }

    private static getHubViewActions(tabId: string): IPivotBarViewAction[] {
        const store = WorkItemsHubViewProvider.getStore();
        const data: WorkItemsHubData = store.getHubDisplayData(tabId);
        if (!data) {
            return [];
        }

        // cache for shallow compare to avoid unnecessary hub updates
        let cachedActions: IPivotBarViewAction[] = WorkItemsHubViewProvider.viewActionCache[tabId];
        if (WorkItemsHubViewProvider.viewActionCache[tabId]) {
            return cachedActions;
        }

        cachedActions = WorkItemsHubViewProvider.viewActionCache[tabId] = [];
        const isRecentlyCompletedPivot = tabId === WorkItemsHubTabUtils.TabIdByTabEnumValueMap[WorkItemsHubTabs.RecentlyCompleted];
        cachedActions.push({
            key: VIEWOPTIONS_SHOW_COMPLETED_ITEMS_KEY,
            name: Resources.CompletedItemsViewOptionName,
            actionType: PivotBarViewActionType.OnOff,
            important: false,
            actionProps: {
                onText: Resources.CompletedItemsShown,
                offText: Resources.CompletedItemsHidden,
                onAriaLabel: Utils_String.format(Resources.CompletedWorkItemsToggleLabel, Resources.CompletedWorkItemsOnText),
                offAriaLabel: Utils_String.format(Resources.CompletedWorkItemsToggleLabel, Resources.CompletedWorkItemsOffText)
            } as IOnOffViewActionProps,
            disabled: isRecentlyCompletedPivot
        });

        return cachedActions;
    }

    private _setInitialViewAction(defaultTabId: string): void {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        const name = state[WorkItemsHubRouteConstants.TabRouteParameterName];

        // if name is empty or invalid, set a default name (persisted tab id or assignedtome if visited first time) in the URL
        if (!name || WorkItemsHubTabUtils.getTabIdByName(name) === null) {
            state[WorkItemsHubRouteConstants.TabRouteParameterName] = defaultTabId;
            historyService.replaceState(state);
            this._initialTabId = defaultTabId;
        } else {
            this._initialTabId = WorkItemsHubTabUtils.getTabIdByName(name);
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        if (WorkItemsHubViewProvider.hubViewState) {
            WorkItemsHubViewProvider.hubViewState.unsubscribe(this._onPivotChanging);
            WorkItemsHubViewProvider.hubViewState.viewOptions.unsubscribe(this._onViewOptionsChanged, VIEW_OPTIONS_CHANGE_EVENT);
        }

        WorkItemsHubViewProvider.mountCount--;

        if (WorkItemsHubViewProvider.mountCount == 0) {
            if (WorkItemsHubViewProvider.hubViewState) {
                WorkItemsHubViewProvider.hubViewState.dispose();
                WorkItemsHubViewProvider.hubViewState = null;
            }

            if (WorkItemsHubViewProvider.store) {
                WorkItemsHubViewProvider.store.dispose();
                WorkItemsHubViewProvider.store = null;
            }
        }
    }

    public static setViewOptions(tabId: string): void {
        const store = WorkItemsHubViewProvider.getStore();

        if (!store.isHubDataInitialized(tabId)) {
            return;
        }

        const { viewOptions } = WorkItemsHubViewProvider.hubViewState;
        const option: IViewOptionsValues = viewOptions.getViewOptions();
        const dataSource = store.getHubFilterDataSource(tabId);

        const isRecentlyCompletedPivot = tabId === WorkItemsHubTabUtils.TabIdByTabEnumValueMap[WorkItemsHubTabs.RecentlyCompleted];
        const shouldShowCompletedItems = isRecentlyCompletedPivot || dataSource.shouldShowCompletedItems();
        option[VIEWOPTIONS_SHOW_COMPLETED_ITEMS_KEY] = shouldShowCompletedItems;

        viewOptions.setViewOptions(option, true /* supress event */);
    }

    private _onPivotChanging = (tabId: string): void  => { 
        PerformanceTelemetryHelper.getInstance(this.context.pageContext).startTabSwitch();

        WorkItemsHubViewProvider.hubViewState.viewOptions.setViewOption(WorkItemsHubRouteConstants.TabRouteParameterName, tabId);

        WorkItemsHubTabSettings.updateRecentTabId(this.context.pageContext, this._project.id, tabId);
    }

    private _onViewOptionsChanged = (value: IViewOptionsValues, action?: string): void  => { 
        const { selectedPivot } = WorkItemsHubViewProvider.hubViewState;
        const tabId = selectedPivot.value;
        const store = WorkItemsHubViewProvider.getStore();
        const data: WorkItemsHubData = store.getHubDisplayData(tabId);

        if (!data) {
            return null;
        }

        if (
            value.hasOwnProperty(VIEWOPTIONS_SHOW_COMPLETED_ITEMS_KEY) &&
            tabId !== WorkItemsHubTabUtils.TabIdByTabEnumValueMap[WorkItemsHubTabs.RecentlyCompleted]
        ) {
            const { pageContext } = this.context;
            const showCompletedItems = value[VIEWOPTIONS_SHOW_COMPLETED_ITEMS_KEY];
            const actionsCreator = WorkItemsHubViewProvider.getActionsCreator();
            const dataSource = store.getHubFilterDataSource(tabId);
            const isCompletedItemsLoaded = dataSource.isCompletedItemsLoaded();

            actionsCreator.setCompletedItemsVisibility(tabId, showCompletedItems);

            WorkItemsHubTabSettings.updateShowCompletedForTab(this.context.pageContext, this._project.id, tabId, showCompletedItems).then(() => {
                // invoke data provider only when user switched to show completed & data is not cached yet
                // also we invoke data provider after setting is persisted to ensure the data provider fetches completed items
                if (showCompletedItems && !isCompletedItemsLoaded) {
                    actionsCreator.invalidateTabData(tabId);
                    actionsCreator.refreshDataProviderAsync(tabId);
                }
            });

            UsageTelemetryHelper.publishCompletedWorkItemsViewOptionTelemetry(pageContext, tabId, showCompletedItems);
        }
    }

    private _getObservableCommands(data: IWorkItemsTabContentData): ObservableArray<IPivotBarAction> {
        const { tabId } = data;
        WorkItemsHubViewProvider.workItemsHubCommandProvider.setTabData(data);

        const store = WorkItemsHubViewProvider.getStore();
        return new ObservableArray(
            WorkItemsHubViewProvider.workItemsHubCommandProvider.getCommands(store.getWorkItemsHubPermission(), store.isSupportedFeature(tabId))
        );
    }

    public render(): JSX.Element {
        return (
            <WorkItemsHubView
                pageContext={this.context.pageContext}
                hubViewState={WorkItemsHubViewProvider.hubViewState}
                store={WorkItemsHubViewProvider.getStore()}
                actionsCreator={WorkItemsHubViewProvider.getActionsCreator()}
                commands={WorkItemsHubViewProvider.observableCommands}
                viewActions={WorkItemsHubViewProvider.observableViewActions}
                ref={this.resolveWorkItemsHubView}
            />
        );
    }

    private resolveWorkItemsHubView = (workItemsHubView: WorkItemsHubView): void  => { 
        if (workItemsHubView) {
            this._workItemsHubShortcutGroup = new WorkItemsHubShortcutGroup(
                WorkItemsHubViewProvider.getWorkItemsHubCommandProvider(),
                workItemsHubView
            );
            this._workItemsHubShortcutGroup.ReRegisterWorkItemsHubShortcuts(this.context.pageContext);
        }
    }

    public static getStore(): WorkItemsHubStore {
        return WorkItemsHubViewProvider.store;
    }

    public static getActionsCreator(): ActionsCreator {
        return WorkItemsHubViewProvider.actionsCreator;
    }

    public static getHubViewState(): IVssHubViewState {
        return WorkItemsHubViewProvider.hubViewState;
    }

    public static getWorkItemsHubCommandProvider(): WorkItemsHubCommandProvider {
        return WorkItemsHubViewProvider.workItemsHubCommandProvider;
    }

    public static updateCommandsActionsAndViewOptions(tabId: string) {
        const store = WorkItemsHubViewProvider.store;
        const newCommands = WorkItemsHubViewProvider.workItemsHubCommandProvider.getCommands(
            store.getWorkItemsHubPermission(),
            store.isSupportedFeature(tabId)
        );
        if (!shallowEquals(WorkItemsHubViewProvider.observableCommands.value, newCommands)) {
            WorkItemsHubViewProvider.observableCommands.value = newCommands;
        }

        const viewActions = WorkItemsHubViewProvider.getHubViewActions(tabId);
        if (!shallowEquals(WorkItemsHubViewProvider.observableViewActions.value, viewActions)) {
            WorkItemsHubViewProvider.observableViewActions.value = viewActions;
        }

        WorkItemsHubViewProvider.setViewOptions(tabId);
    }
}

VssComponent.register("work-items-hub-view-component", WorkItemsHubViewComponent);
